import React, { useMemo } from "react";
import MaxContent from "../../layout/MaxContent";
import { useStaticQuery, graphql, Link } from "gatsby";
import Button from '../Inputs/Button'
import { useDevice } from "../Hooks";
import { IBlog, ColourCategoryMap } from "./Article_Listing/ArticleListing";
import MakeSlug from '../../helpers/makeSlug'
import { fixStrapiUrl } from "../../helpers/strapiUrl";

import { TitleType } from "../Types";
import { getTitleHTMLTag } from "../../helpers/tools";

interface StrapiData {
  data: IFeaturedArticles;
}

interface IFeaturedArticles {
  Title: string;
  SelectTitleType: {
    TitleTypeSelection: TitleType;
  };
  ButtonLabel: string
  ButtonUrl: string
  NoFollow?: boolean
  NewTab?: boolean
}

const FeaturedArticles: React.FC<StrapiData> = ({ data }) => {
  const { isMobile, isMobileTablet } = useDevice();
  const query = useStaticQuery(BlogQuery);
  const blogs = query.strapi?.blogs || [];
  const latest = useMemo(() =>
    [...blogs].sort((a: IBlog, b: IBlog) => new Date(b.publishedAt).valueOf() - new Date(a.publishedAt).valueOf()),
    [blogs]
  );

  let title = getTitleHTMLTag(
    data.Title,
    data.SelectTitleType?.TitleTypeSelection || "H1",
    "font-bold text-[26px] leading-[36px] lg:text-[35px] lg:leading-[45px] text-purple"
  );

  return (
    <MaxContent size="content">
      <div className="flex flex-col items-center pt-20 pb-20">
        {data.Title && title}
        <div className=" flex flex-col md:flex-row   gap-[30px] pt-4">
          {latest &&
            latest
              .slice(0, isMobileTablet ? 3 : 4)
              .map((blog: IBlog) => (
                <FeaturedArticlesBlog key={blog.documentId ?? blog.CustomUrl ?? blog.Title} {...blog} />
              ))}
        </div>

        <div className="w-[325px] h-[60px] mt-[80px]">
          {/* No <Link> wrapper here: Button already renders the <a>, and an <a>
              inside an <a> is invalid HTML. The parser hoists the inner anchor
              out of the outer one, so the DOM never matches what React rendered
              on the server = hydration mismatch (#418) and, since this sits
              outside any Suspense boundary, a full client re-render (#423). */}
          <Button
            label={data.ButtonLabel}
            href={data.ButtonUrl ?? '/blog'}
            className="no-underline"
            secondary={true}
            noFollow={data.NoFollow ?? false}
            newTab={data.NewTab ?? false}
          />
        </div>
      </div>
    </MaxContent>
  );
};

export const FeaturedArticlesBlog = (blog: IBlog) => {
  let url = `/blog/${blog.CustomUrl ?? MakeSlug(blog.Title)}`;

  if (blog.Category) {
    url = `/blog/${MakeSlug(blog.Category.toLowerCase().replace(/_/g, '-'))}/${blog.CustomUrl ?? MakeSlug(blog.Title)}`;
  }

  if (blog.Category === "Conseil_et_fiches_pratiques") {
    url = `/blog/conseils/${blog.CustomUrl ?? MakeSlug(blog.Title)}`;
  }

  if (blog.Category === "Edito_et_actus") {
    url = `/blog/edito/${blog.CustomUrl ?? MakeSlug(blog.Title)}`;
  }

  return (
    <div className="flex flex-row md:flex-col md:w-full min-h-[94px] md:h-full pt-5">
      {blog.PrimaryImage?.url ? (
        <img
          className="md:min-w-[190px] w-[160px] md:w-auto md:max-w-full h-full md:h-[243px] mb-[30px] rounded-md object-cover"
          src={fixStrapiUrl(blog.PrimaryImage?.url)}
          alt={blog.PrimaryImage?.alternativeText}
          loading="lazy"
          decoding="async"
        />
      ): (
        <img
          src={blog.ImageURL}
          alt={blog.ImageAlt}
          className="md:min-w-[190px] w-[160px] md:w-auto md:max-w-full h-full md:h-[243px] mb-[30px] rounded-md object-cover"
          loading="lazy"
          decoding="async"
        />
      )}
      

      <div className="flex flex-col ml-[11px] md:ml-0">
        <div
          className="rounded-full w-fit font-MW font-bold text-[10px] leading-[12.5px] px-[8px] py-[3px] md:mb-[20px]"
          style={{
            backgroundColor: ColourCategoryMap[blog.Category]?.hex || ColourCategoryMap.default.hex,
            color: ColourCategoryMap[blog.Category]?.textColor || "#FFFFFF",
          }} >
          {blog.Category && blog.Category.toUpperCase().replaceAll("_", " ")}
        </div>

        <Link to={url} className="group no-underline text-placeholder hover:text-purple cursor-pointer">
          <p className="text-link-lg font-bold font-DM group-hover:underline">
            {blog.Title}
          </p>
        </Link>
      </div>
    </div>
  );
};

const BlogQuery = graphql`
  query {
    strapi {
      blogs(pagination: { limit: 100 }) {
        documentId
        Title
        CustomUrl
        Category
        Description
        PrimaryImage {
          alternativeText
          url
        }
        ImageURL
        ImageAlt
        createdAt
        updatedAt
        publishedAt
      }
    }
  }
`;

export default FeaturedArticles;
